import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, tap } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AppService } from '../../app.service';
import { IConfigService } from '../../config/iconfigservice';
import { BaseService } from '../base.service';
import UserSetting from '@Models/global/user-settings';

@Injectable({ providedIn: 'root' })
export class UserSettingsService extends BaseService {

    public static readonly DASHBOARD_CASES_FILTERS_KEY = 'dashboardCasesFilters';
    public static readonly DASHBOARD_TASKS_FILTERS_KEY = 'dashboardTasksFilters';
    public static readonly DASHBOARD_DATE_RANGE_KEY = 'dashboardDateRange';
    public static readonly UWREQUIREMENT_NOTES_FILTER_KEY = 'uwRequirementNotesFilter';
    public static readonly SIDEBAR_NOTE_TYPE_FILTER_KEY = 'sidebarNoteTypeFilter';
    public static readonly CASE_SUMMARY_SIDEBAR_OPEN_KEY = 'caseSummarySidebarOpen';

    private readonly cache = new Map<string, unknown>();

    constructor(appService: AppService, configService: IConfigService, httpClient: HttpClient) {
        super(appService, configService, httpClient);
    }

    private getSettings(): Observable<boolean> {
        const url = `${this.basePublicApiUrl}user/settings`;
        return this.getData<UserSetting[]>(url, undefined, true).pipe(
            map(response => {
                if (!response) return false;

                response.forEach(setting => {
                    try {
                        const parsedValue = JSON.parse(setting.settingValue);
                        if (!this.cache.has(setting.settingKey)) {
                            this.cache.set(setting.settingKey, parsedValue);
                        }
                    } catch {
                        // If parsing fails, we skip caching this setting
                    }
                });  
                
                return true;
            }),
            catchError(() => of(false))
        );
    }

    getSetting<T>(key: string): Observable<T | null> {
        if (this.cache.has(key)) {
            return of(this.cache.get(key) as T);
        }

        return this.getSettings().pipe(
            map(() => {
                if (this.cache.has(key)) {
                    return this.cache.get(key) as T;
                }
                return null;
            })
        );
    }

    saveSetting<T>(key: string, value: T): Observable<void> {
        this.cache.set(key, value);
        const url = `${this.basePublicApiUrl}user/settings/${encodeURIComponent(key)}`;
        return this.putData<void>(url, { value: JSON.stringify(value) }, null, true).pipe(
            map(() => void 0),
            catchError(() => of(void 0))
        );
    }
}
